import React, { useState, useEffect } from "react";
import { 
  Database, 
  History, 
  Settings, 
  Search, 
  Download, 
  RefreshCcw, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Eye,
  ChevronRight,
  Clock,
  ShieldCheck
} from "lucide-react";
import authAxios from "../../api/authAxios";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";

const BackupManagement = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [settings, setSettings] = useState({ auto_backup: false, frequency: "DAILY" });
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    const ownerId = searchParams.get("ownerId");
    fetchCustomers(ownerId);
  }, []);

  // Auto-fetch data when tab or selected owner changes
  useEffect(() => {
    if (selectedOwner) {
      if (activeTab === "history") {
        fetchHistory(selectedOwner.id);
      } else if (activeTab === "settings") {
        fetchSettings(selectedOwner.id);
      }
    }
  }, [activeTab, selectedOwner?.id]);

  const fetchCustomers = async (preSelectId) => {
    setLoading(true);
    try {
      const response = await authAxios.get("/api/backup/customers/");
      const data = response.data;
      setCustomers(data);

      if (preSelectId) {
        const owner = data.find(c => c.id.toString() === preSelectId);
        if (owner) {
          setSelectedOwner(owner);
          setActiveTab("history");
          fetchHistory(owner.id);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (ownerId) => {
    setHistoryLoading(true);
    try {
      const response = await authAxios.get(`/api/backup/history/${ownerId}/`);
      setHistory(response.data);
    } catch (error) {
      toast.error("Failed to fetch backup history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchSettings = async (ownerId) => {
    setSettingsLoading(true);
    try {
      const response = await authAxios.get(`/api/backup/settings/${ownerId}/`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to fetch settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleBackup = async (ownerId) => {
    try {
      toast.loading("Initiating backup...", { id: "backup" });
      await authAxios.post(`/api/backup/trigger/${ownerId}/`);
      toast.success("Backup successful!", { id: "backup" });
      fetchCustomers();
      // If we are looking at this owner's history, refresh it
      if (selectedOwner && selectedOwner.id === ownerId) {
        fetchHistory(ownerId);
      }
    } catch (error) {
      toast.error("Backup failed", { id: "backup" });
    }
  };

  const handleRestore = async (recordId) => {
    if (!window.confirm("Are you sure you want to restore this backup? Current data will be replaced!")) return;

    try {
      toast.loading("Restoring data...", { id: "restore" });
      await authAxios.post(`/api/backup/restore/${recordId}/`);
      toast.success("Data restored successfully!", { id: "restore" });
    } catch (error) {
      toast.error("Restore failed", { id: "restore" });
    }
  };

  const handleUpdateSettings = async (ownerId) => {
    try {
      setSettingsLoading(true);
      await authAxios.put(`/api/backup/settings/${ownerId}/`, settings);
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this backup record? This action cannot be undone.")) return;
    
    try {
      await authAxios.delete(`/api/backup/record/${recordId}/`);
      toast.success("Backup record deleted");
      if (selectedOwner) fetchHistory(selectedOwner.id);
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const handleClearBackups = async (ownerId) => {
    if (!window.confirm("Are you sure you want to delete ALL backups for this customer? This will free up storage but you won't be able to restore old data.")) return;

    try {
      toast.loading("Clearing backups...", { id: "clear" });
      await authAxios.delete(`/api/backup/clear/${ownerId}/`);
      toast.success("All backups cleared", { id: "clear" });
      fetchCustomers();
      if (selectedOwner && selectedOwner.id === ownerId) {
        setHistory([]);
      }
    } catch (error) {
      toast.error("Failed to clear backups", { id: "clear" });
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.phone.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (c.first_name && c.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (c.last_name && c.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (c.business_type && c.business_type.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const renderCustomersTab = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Customer Backups</h3>
          <p className="text-sm text-slate-500">Manage and monitor data backups for all business accounts</p>
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search customers..."
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900/50">
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer / Business</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Backup</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              [1, 2, 3].map((i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-6 py-4"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div></td>
                  <td className="px-6 py-4"><div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-16"></div></td>
                  <td className="px-6 py-4 text-right"><div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-24 ml-auto"></div></td>
                </tr>
              ))
            ) : filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-slate-500">No customers found</td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition">
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900 dark:text-white">
                       {customer.last_name || customer.business_name || "Unknown Store"}
                    </div>
                    <div className="text-xs text-slate-500 font-medium">
                       {customer.phone} • {customer.business_type || "Standard Business"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    {customer.last_backup ? new Date(customer.last_backup).toLocaleString() : "Never"}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      customer.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' :
                      customer.status === 'FAILED' ? 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400' :
                      'bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-400'
                    }`}>
                      {customer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button 
                      onClick={() => handleBackup(customer.id)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition"
                      title="Run Backup Now"
                    >
                      <RefreshCcw className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedOwner(customer);
                        setActiveTab("history");
                        fetchHistory(customer.id);
                      }}
                      className="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
                      title="View History"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedOwner(customer);
                        setActiveTab("settings");
                        fetchSettings(customer.id);
                      }}
                      className="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
                      title="Backup Settings"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleClearBackups(customer.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition"
                      title="Clear All Backups"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => {
            setActiveTab("customers");
            setSelectedOwner(null);
          }}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Backup History: {selectedOwner?.phone}
          </h3>
          <p className="text-sm text-slate-500">View and restore previous data snapshots</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900/50">
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date & Time</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {historyLoading ? (
              [1, 2, 3].map(i => (
                <tr key={i} className="animate-pulse">
                   <td colSpan="4" className="px-6 py-4"><div className="h-4 bg-slate-100 dark:bg-slate-700 rounded w-full"></div></td>
                </tr>
              ))
            ) : history.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-slate-500">No backup records found</td>
              </tr>
            ) : (
              history.map((record, index) => (
                <tr key={record.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition">
                  <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-white">
                    {new Date(record.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      record.type === 'AUTO' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400' : 
                      'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400'
                    }`}>
                      {record.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                     <span className={`flex items-center gap-1.5 text-xs font-medium ${
                      record.status === 'SUCCESS' ? 'text-emerald-600' : 
                      record.status === 'FAILED' ? 'text-red-600' : 'text-slate-500'
                    }`}>
                      {record.status === 'SUCCESS' ? <CheckCircle className="w-3.5 h-3.5" /> : 
                       record.status === 'FAILED' ? <AlertCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                      {record.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    {record.status === 'SUCCESS' && (
                      <button 
                        onClick={() => handleRestore(record.id)}
                        className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                      >
                        Restore
                      </button>
                    )}
                    {record.file_url && (
                       <a 
                        href={record.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white transition inline-block"
                       >
                         <Download className="w-4 h-4" />
                       </a>
                    )}
                    <button 
                      onClick={() => handleDeleteRecord(record.id)}
                      disabled={index === 0}
                      className={`p-2 rounded-lg transition ${
                        index === 0 
                        ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' 
                        : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10'
                      }`}
                      title={index === 0 ? "Cannot delete the last backup" : "Delete Record"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => {
            setActiveTab("customers");
            setSelectedOwner(null);
          }}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Backup Settings: {selectedOwner?.phone}
          </h3>
          <p className="text-sm text-slate-500">Configure automated data protection rules</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-6 shadow-sm">
        <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
          <div className="flex items-center gap-3">
             <div className="p-2 bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 rounded-lg">
                <RefreshCcw className="w-5 h-5" />
             </div>
             <div>
               <p className="font-medium text-slate-900 dark:text-white">Enable Auto Backup</p>
               <p className="text-xs text-slate-500">Automatically capture data snapshots without manual trigger</p>
             </div>
          </div>
          <button 
            onClick={() => setSettings({...settings, auto_backup: !settings.auto_backup})}
            className={`w-12 h-6 rounded-full transition-colors relative ${settings.auto_backup ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-700'}`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${settings.auto_backup ? 'left-7' : 'left-1'}`} />
          </button>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Frequency</label>
          <div className="grid grid-cols-2 gap-4">
            {['DAILY', 'WEEKLY'].map((freq) => (
              <button
                key={freq}
                onClick={() => setSettings({...settings, frequency: freq})}
                className={`px-4 py-3 rounded-lg border text-sm font-medium transition ${
                  settings.frequency === freq 
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600' 
                  : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                }`}
              >
                {freq === 'DAILY' ? 'Daily (Recommended)' : 'Weekly'}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-4 flex items-center gap-3 border-t border-slate-100 dark:border-slate-700">
           <ShieldCheck className="w-5 h-5 text-emerald-500" />
           <p className="text-xs text-slate-500 italic">Backups are stored with AES-256 encryption in a secure cloud environment.</p>
        </div>

        <div className="pt-4">
          <button 
            onClick={() => handleUpdateSettings(selectedOwner.id)}
            disabled={settingsLoading}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-lg shadow-indigo-500/20 transition disabled:opacity-50"
          >
            {settingsLoading ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
          <Database className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Backup</h2>
          <p className="text-slate-500">Enterprise data protection and disaster recovery</p>
        </div>
      </div>

      <div className="flex border-b border-slate-200 dark:border-slate-700 gap-8">
        <button 
          onClick={() => setActiveTab("customers")}
          className={`pb-4 px-1 text-sm font-medium transition relative ${
            activeTab === 'customers' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          Customer Backups
          {activeTab === 'customers' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />}
        </button>
        {selectedOwner && (
          <>
            <button 
              onClick={() => {
                setActiveTab("history");
                fetchHistory(selectedOwner.id);
              }}
              className={`pb-4 px-1 text-sm font-medium transition relative ${
                activeTab === 'history' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              History: {selectedOwner.phone}
              {activeTab === 'history' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />}
            </button>
            <button 
              onClick={() => {
                setActiveTab("settings");
                fetchSettings(selectedOwner.id);
              }}
              className={`pb-4 px-1 text-sm font-medium transition relative ${
                activeTab === 'settings' ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Settings
              {activeTab === 'settings' && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />}
            </button>
          </>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === "customers" && renderCustomersTab()}
          {activeTab === "history" && renderHistoryTab()}
          {activeTab === "settings" && renderSettingsTab()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default BackupManagement;
